import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';

import { blankStudent, initialStudentsReponse } from './constants/inviteStudentsDefaults';
import { StyledAlertAnchor } from './styles/styled-components';
import {
    type InviteStudentsAlert,
    type InviteStudentsResponse,
    type InviteStudentsResponseBody,
    type InviteTabTypes,
    type LoadingStates,
    type Student
} from './types/inviteStudentsTypes';
import {
    findDuplicateEmailIndexes,
    findDuplicateExtUserIdIndexes,
    validateStudentInvitations
} from './utils/inviteStudentsValidation';
import { removeBlankStudents } from './utils/removeBlankStudents';
import { trimForPost } from './utils/trimForPost';
import { ADMIN_SETTINGS, CLASS_INFO, INVITE_STUDENTS_API } from '../../config/apiPaths';
import { DEFAULT_SCHOOL_SETTINGS } from '../../config/settingsConfig';
import { useAdminSettings } from '../../context/api';
import { localStorageKeys } from '../../localStorageKeys';
import { type ClassInfo, type GetAdminSettings } from '../../types/apiTypes';
import { getUrls } from '../../utils/url';

type State = {
    classId?: string;
    classUuid?: string;
    isDemo?: string;
    token?: string;
    userUuid?: string;
    uuid?: string;
};

const useInviteStudents = (initialState: State = {}) => {
    const { t } = useTranslation('invite_students', { useSuspense: false });
    const { data: settings = DEFAULT_SCHOOL_SETTINGS } = useAdminSettings();
    const { api, vanityUrl } = getUrls();

    const [activeTab, setActiveTab] = useState<InviteTabTypes>('simple');
    const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
    const [duplicateEmailIndexes, setDuplicateEmailIndexes] = useState<number[]>([]);
    const [duplicateExtUserIdIndexes, setDuplicateExtUserIdIndexes] = useState<number[]>([]);
    const [students, setStudents] = useState<Student[]>([{ ...blankStudent }]);
    const [sendEmails, setSendEmails] = useState<boolean>(false);
    const [includeCalendarInvite, setIncludeCalendarInvite] = useState<boolean>(false);
    const [inviteStudentsStatus, setInviteStudentsStatus] = useState<LoadingStates>('INITIAL');
    const [inviteStudentsResult, setInviteStudentsResult] = useState<InviteStudentsResponse>(initialStudentsReponse);
    const [alerts, setAlerts] = useState<InviteStudentsAlert[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const getAdminSettings = async () => {
            const { data } = await axios.get<GetAdminSettings>(`${api}${ADMIN_SETTINGS}`);

            setSendEmails(data.data.send_invite_emails);
            setIncludeCalendarInvite(data.data.send_invite_emails);
            setLoading(false);
        };

        getAdminSettings();
    }, [api]);

    useEffect(() => {
        if (settings) {
            setSendEmails(settings.send_invite_emails);
            setIncludeCalendarInvite(settings.send_invite_emails);
            setLoading(false);
        }
    }, [settings]);

    // TODO: Shim to reload `roster` when clicking the 'go back' button - these params are required to load the view - DBvJ 10.1.21
    const search = new URLSearchParams(window.location.search);
    const queryParams = {
        classId: initialState.classId ?? search.get('class_id'),
        classUuid: initialState.classUuid ?? search.get('class_uuid'),
        isDemo: initialState.isDemo ?? search.get('isDemo'),
        token: initialState.token ?? search.get('token'),
        userUuid: initialState.userUuid ?? window.localStorage.getItem(localStorageKeys.userUuid),
        uuid: initialState.uuid ?? search.get('uuid')
    };

    const { data: classInfo } = useQuery({
        queryKey: [CLASS_INFO, queryParams.classUuid],
        enabled: !!queryParams.classUuid,
        queryFn: async () => {
            try {
                const { data } = await axios.get<ClassInfo>(
                    `${api}${CLASS_INFO}?class_id=${queryParams.classUuid}&with_info=true`
                );
                return data;
            } catch (err) {
                console.error(err);
                return undefined;
            }
        }
    });

    const returnToRosterUrl =
        queryParams.isDemo === 'true'
            ? `${vanityUrl}/roster/roster.html?user_uuid=${queryParams.userUuid}&class_uuid=${queryParams.classUuid}&uuid=${queryParams.uuid}`
            : `${vanityUrl}/react/roster/${queryParams.classUuid}/?class_id=${queryParams.classId}&user_uuid=${queryParams.userUuid}&token=${queryParams.token}`;

    const hasValidationErrors = validateStudentInvitations(students).length !== 0;
    const canSubmit = !hasValidationErrors && inviteStudentsStatus !== 'LOADING';

    const validationErrorAlert: InviteStudentsAlert = {
        category: 'validationError',
        // eslint-disable-next-line arrow-body-style
        message: () => {
            return (
                <span>
                    {t('alerts.validation-error-1')}{' '}
                    <StyledAlertAnchor onClick={() => setActiveTab('error')}>
                        {t('alerts.validation-error-2')}
                    </StyledAlertAnchor>
                    .
                </span>
            );
        },
        severity: 'error'
    };

    const noStudentsErrorAlert: InviteStudentsAlert = {
        category: 'validationError',
        message: t('alerts.no-students'),
        severity: 'error'
    };

    const handleStudentChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name: field, value } = event.target;

        setStudents(
            students.map((student, i) => {
                if (i === index) {
                    return { ...student, [field]: value };
                }

                return student;
            })
        );
    };

    const resetInviteStatus = () => {
        setInviteStudentsStatus('INITIAL');
        setInviteStudentsResult(initialStudentsReponse);
    };

    const setErrors = (studentsToUse: Student[]) => {
        setErrorIndexes(validateStudentInvitations(studentsToUse));
        setDuplicateEmailIndexes(findDuplicateEmailIndexes(studentsToUse));
        setDuplicateExtUserIdIndexes(findDuplicateExtUserIdIndexes(studentsToUse));
    };

    const sendInvitations = async () => {
        const buildInviteFailEmailList = (label: string, inviteFailures: Array<Record<string, string>>) => {
            const emails = inviteFailures.map((i) => Object.keys(i)[0]);
            return (
                <>
                    <p>{label}</p>
                    <ul>
                        {emails.map((email, index) => (
                            <li key={index}>{email}</li>
                        ))}
                    </ul>
                </>
            );
        };

        const cleanedStudentsInput = removeBlankStudents(students);

        if (cleanedStudentsInput.length === 0) {
            setAlerts([...alerts, noStudentsErrorAlert]);
            return;
        }

        const newErrorIndexes = validateStudentInvitations(students);
        setErrors(students);

        if (newErrorIndexes.length > 0) {
            setAlerts([...alerts, validationErrorAlert]);
            return;
        }

        setInviteStudentsStatus('LOADING');

        try {
            const response: AxiosResponse<InviteStudentsResponseBody> = await axios.post(
                `${api}${INVITE_STUDENTS_API}`,
                {
                    userUuid: queryParams.userUuid,
                    classId: queryParams.classId,
                    classUuid: queryParams.classUuid,
                    students: trimForPost(cleanedStudentsInput),
                    sendEmails,
                    includeCalendarInvite
                },
                {
                    validateStatus(status) {
                        return status >= 200;
                    }
                }
            );

            setInviteStudentsResult({ status: response.status, body: response.data });

            if (response.status >= 400) {
                setInviteStudentsStatus('ERROR');
            } else {
                const partialSuccess =
                    response.data?.failedEmailSends?.length || response.data?.failedEnrollments?.length;

                const newStatus = partialSuccess ? 'PARTIAL-SUCCESS' : 'SUCCESS';
                setInviteStudentsStatus(newStatus);

                const failAlerts: InviteStudentsAlert[] = [];

                if (response.data?.failedEmailSends?.length) {
                    const emailList = buildInviteFailEmailList(
                        t('alerts.invite-email-failure'),
                        response.data.failedEmailSends
                    );
                    const emailSendErrorAlert: InviteStudentsAlert = {
                        category: 'uploadError',
                        message: emailList,
                        severity: 'error'
                    };
                    failAlerts.push(emailSendErrorAlert);
                }
                if (response.data?.failedEnrollments?.length) {
                    const emailList = buildInviteFailEmailList(
                        t('alerts.invite-enroll-failure'),
                        response.data.failedEnrollments
                    );
                    const enrollSendErrorAlert: InviteStudentsAlert = {
                        category: 'uploadError',
                        message: emailList,
                        severity: 'error'
                    };
                    failAlerts.push(enrollSendErrorAlert);
                }
                if (failAlerts.length) {
                    setAlerts([...alerts, ...failAlerts]);
                }
            }
        } catch (error) {
            setInviteStudentsResult({ status: 500, body: { error: (error as AxiosError).response?.statusText ?? '' } });
            setInviteStudentsStatus('ERROR');
        }
    };

    const isDuplicateEmail = (index: number): boolean => duplicateEmailIndexes.includes(index);

    const isDuplicateExtUserId = (index: number): boolean => duplicateExtUserIdIndexes.includes(index);

    const addStudent = (student: Student) => {
        setStudents((current) => {
            if (current.find((s) => s.email === student.email)) {
                return current;
            }

            // remove the blank student if it exists
            const blankStudentIndex = current.findIndex((s) => s.email === '');
            if (blankStudentIndex !== -1) {
                current.splice(blankStudentIndex, 1);
            }

            return [...current, student, { ...blankStudent }];
        });
    };

    const isStudentAdded = useCallback((email: string) => students.some((s) => s.email === email), [students]);

    return {
        classInfo,
        canSubmit,
        hasValidationErrors,
        returnToRosterUrl,
        validationErrorAlert,
        activeTab,
        setActiveTab,
        alerts,
        setAlerts,
        setDuplicateEmailIndexes,
        errorIndexes,
        setErrorIndexes,
        inviteStudentsStatus,
        setInviteStudentsStatus,
        students,
        setStudents,
        inviteStudentsResult,
        sendInvitations,
        setErrors,
        handleStudentChange,
        isDuplicateEmail,
        resetInviteStatus,
        sendEmails,
        setSendEmails,
        includeCalendarInvite,
        setIncludeCalendarInvite,
        settings,
        loading,
        addStudent,
        isStudentAdded,
        isDuplicateExtUserId,
        setDuplicateExtUserIdIndexes
    };
};

const InviteStudentsContainer = createContainer(useInviteStudents);

export default InviteStudentsContainer;
